import { Box, Flex, Grid, Text, useBreakpointValue, useColorModeValue, type ChakraProps } from '@chakra-ui/react';
import type { Fixture } from '@/models/fixture';
import { fixtureSideDisplayName, formatFixtureKickoffLondon } from '@/helpers';
import { formatFixtureMetaLine } from '@/helpers/fixtures/liveMatchDisplay';
import { Flag } from './Flag';
import { TeamDetailTrigger } from './teamDetail/TeamDetailTrigger';
import { isClickableTeamCountry } from './teamDetail/teamDetailUtils';

/** Fixed center column (score / vs) so away flags align across fixture, live, and result rows. */
export const FIXTURE_MATCH_CENTER_COLUMN = {
  base: '3rem',
  sm: '3.5rem',
};

/** Shared column widths so home/away flags line up across fixture and live rows. */
export const FIXTURE_MATCH_GRID_COLUMNS = {
  base: `1.5rem minmax(0, 1fr) ${FIXTURE_MATCH_CENTER_COLUMN.base} 1.5rem minmax(0, 1fr)`,
  sm: `1.5rem minmax(0, 1.15fr) ${FIXTURE_MATCH_CENTER_COLUMN.sm} 1.5rem minmax(0, 1.15fr)`,
};

/** Fixed width for accordion chevron column (matches Chakra AccordionIcon). */
export const FIXTURE_MATCH_CHEVRON_COLUMN = '1.25rem';

/** Same as fixture grid plus chevron column so live/result rows align with fixtures. */
export const FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON = {
  base: `${FIXTURE_MATCH_GRID_COLUMNS.base} ${FIXTURE_MATCH_CHEVRON_COLUMN}`,
  sm: `${FIXTURE_MATCH_GRID_COLUMNS.sm} ${FIXTURE_MATCH_CHEVRON_COLUMN}`,
};

function FixtureTeamCell(props: {
  fixture: Fixture;
  side: 'home' | 'away';
  fontSize: ChakraProps['fontSize'];
  nameLines?: number;
  svg?: boolean;
  nameCol: number;
  flagCol: number;
}) {
  const team = props.side === 'home' ? props.fixture.homeTeam : props.fixture.awayTeam;
  const name = fixtureSideDisplayName(props.fixture, props.side);
  const clickable = isClickableTeamCountry(team);

  const flag = (
    <Flag country={team} w={6} borderRadius='3px' flexShrink={0} svg={props.svg} justifySelf='start' />
  );

  const nameEl = (
    <Text
      fontSize={props.fontSize}
      fontWeight='semibold'
      lineHeight='short'
      noOfLines={props.nameLines ?? 2}
      textAlign='left'
      minW={0}
    >
      {name}
    </Text>
  );

  const flagCell = clickable ? (
    <TeamDetailTrigger country={team} source='fixtures' variant='inline' justifySelf='start'>
      {flag}
    </TeamDetailTrigger>
  ) : (
    flag
  );

  const nameCell = clickable ? (
    <TeamDetailTrigger country={team} source='fixtures' variant='compact'>
      {nameEl}
    </TeamDetailTrigger>
  ) : (
    nameEl
  );

  return (
    <>
      <Box gridColumn={props.flagCol} gridRow={1} display='flex' alignItems='center'>
        {flagCell}
      </Box>
      <Box gridColumn={props.nameCol} gridRow={1} display='flex' alignItems='center' minW={0}>
        {nameCell}
      </Box>
    </>
  );
}

export function FixtureMatchRow(props: {
  fixture: Fixture;
  fontSize?: ChakraProps['fontSize'];
  metaFontSize?: ChakraProps['fontSize'];
  nameLines?: number;
  svg?: boolean;
  bracketLabel?: string;
  /** When set, shows knockout / bracket title above the match row. */
  showBracketLabel?: boolean;
  /** When set, city appears in the header centred above vs (not in the kickoff meta line). */
  headerCity?: string;
  /** Knockout accordion rows include a chevron column; map cards omit it. */
  includeChevronColumn?: boolean;
}) {
  const includeChevron = props.includeChevronColumn ?? true;
  const columnsWithChevron =
    useBreakpointValue(FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON) ?? FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON.base;
  const columnsWithoutChevron =
    useBreakpointValue(FIXTURE_MATCH_GRID_COLUMNS) ?? FIXTURE_MATCH_GRID_COLUMNS.base;
  const columns = includeChevron ? columnsWithChevron : columnsWithoutChevron;
  const defaultFontSize = useBreakpointValue({ base: 13, sm: 15 });
  const defaultMetaFontSize = useBreakpointValue({ base: 10, sm: 12 });
  const defaultNameLines = useBreakpointValue({ base: 1, sm: 2 }) ?? 1;
  const columnGap = useBreakpointValue({ base: 2, sm: 3 }) ?? 2;
  const fontSize = props.fontSize ?? defaultFontSize;
  const metaFontSize = props.metaFontSize ?? defaultMetaFontSize;
  const nameLines = props.nameLines ?? defaultNameLines;
  const vsColor = useColorModeValue('gray.500', 'gray.400');
  const metaColor = useColorModeValue('gray.600', 'gray.400');
  const rowBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const matchLabelColor = useColorModeValue('gray.700', 'gray.300');

  const kickoff = formatFixtureKickoffLondon(props.fixture.date.toDate());
  const bracketTitle = props.bracketLabel;
  const headerCity = props.headerCity;
  const metaLine = formatFixtureMetaLine(kickoff, props.fixture, { omitCity: Boolean(headerCity) });

  return (
    <Box w='full'>
      {props.showBracketLabel && bracketTitle && headerCity ? (
        <Flex align='center' gap={2} w='full' pb={2}>
          <Text
            flex='1'
            fontSize={metaFontSize}
            fontWeight='semibold'
            color={matchLabelColor}
            lineHeight='short'
            textAlign='left'
            minW={0}
          >
            {bracketTitle}
          </Text>
          <Text
            flex='1'
            fontSize={metaFontSize}
            fontWeight='semibold'
            color={matchLabelColor}
            lineHeight='short'
            textAlign='center'
            minW={0}
            whiteSpace='nowrap'
            noOfLines={1}
          >
            {headerCity}
          </Text>
          <Box flex='1' minW={8} aria-hidden />
        </Flex>
      ) : props.showBracketLabel && bracketTitle ? (
        <Text
          fontSize={metaFontSize}
          fontWeight='semibold'
          color={matchLabelColor}
          lineHeight='short'
          textAlign='left'
          pb={2}
        >
          {bracketTitle}
        </Text>
      ) : null}
      <Grid
        templateColumns={columns}
        columnGap={columnGap}
        rowGap={2}
        alignItems='center'
        w='full'
        py={2}
        px={{ base: 2, sm: 3 }}
        borderRadius='md'
        borderWidth='1px'
        borderColor={borderColor}
        bg={rowBg}
      >
        <FixtureTeamCell
          fixture={props.fixture}
          side='home'
          fontSize={fontSize}
          nameLines={nameLines}
          svg={props.svg}
          flagCol={1}
          nameCol={2}
        />
        <Text gridColumn={3} gridRow={1} fontSize='xs' fontWeight='bold' color={vsColor} textAlign='center'>
          vs
        </Text>
        <FixtureTeamCell
          fixture={props.fixture}
          side='away'
          fontSize={fontSize}
          nameLines={nameLines}
          svg={props.svg}
          flagCol={4}
          nameCol={5}
        />
        {includeChevron ? <Box gridColumn={6} gridRow={1} aria-hidden /> : null}
        <Text
          gridColumn='1 / -1'
          gridRow={2}
          fontSize={metaFontSize}
          color={metaColor}
          lineHeight='short'
          textAlign='left'
        >
          {metaLine}
        </Text>
      </Grid>
    </Box>
  );
}
